// react
import React, { useState, useEffect, useMemo } from "react";

// react-router-dom
import { useNavigate } from "react-router-dom";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TableFooter,
  TablePagination,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { KeyboardArrowLeftOutlined as KeyboardArrowLeftOutlinedIcon, KeyboardArrowRightOutlined as KeyboardArrowRightOutlinedIcon, Search as SearchIcon } from "@mui/icons-material";

// utils
import convertTimestamp from "utils/convertTimestamp";

// A ---------------------------------------------------------------------- M

const ClientsTableToolbar = ({ searchQuery, handleSearchChange }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Grid container alignItems="center" spacing={2} mb={2}>
      <Grid item xs={12} sm={6}>
        <Typography variant="h6" fontWeight={700}>
          {t("customer_records")}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} container justifyContent="flex-end" spacing={1}>
        <Grid item xs={12} sm={7}>
          <TextField
            fullWidth
            placeholder={`${t("search_by_name")}...`}
            value={searchQuery}
            onChange={handleSearchChange}
            size="small"
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                backgroundColor: theme.palette.background.paper,
                "&:hover fieldset": { borderColor: theme.palette.primary.main },
              },
            }}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

const TablePaginationActions = ({ count, page, rowsPerPage, onPageChange }) => {
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={(e) => onPageChange(e, page - 1)} disabled={page === 0} aria-label="previous page">
        {isRtl ? <KeyboardArrowRightOutlinedIcon /> : <KeyboardArrowLeftOutlinedIcon />}
      </IconButton>
      <IconButton onClick={(e) => onPageChange(e, page + 1)} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
        {isRtl ? <KeyboardArrowLeftOutlinedIcon /> : <KeyboardArrowRightOutlinedIcon />}
      </IconButton>
    </Box>
  );
};

TablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

const CustomersTable = ({ clients }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredClients = useMemo(() => clients.filter((client) => client.companyName.toLowerCase().includes(searchQuery.toLowerCase())), [clients, searchQuery]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, clients]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredClients.length) : 0;

  return (
    <Box>
      <ClientsTableToolbar searchQuery={searchQuery} handleSearchChange={(e) => setSearchQuery(e.target.value)} />

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}CC)`,
          backdropFilter: "blur(6px)",
          border: isMobile ? "none" : `1px solid ${theme.palette.divider}`,
        }}
      >
        <Table>
          {!isMobile && (
            <TableHead sx={{ background: `linear-gradient(90deg, ${theme.palette.primary.main}33, ${theme.palette.primary.dark}11)` }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>{t("name")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("creation_datetime")}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{t("email")}</TableCell>
              </TableRow>
            </TableHead>
          )}

          <TableBody>
            {(isMobile ? filteredClients : filteredClients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)).map((client) =>
              isMobile ? (
                <Box
                  key={client.id}
                  onClick={() => navigate(`/customers/${client.id}`, { state: { client } })}
                  sx={{
                    p: 2,
                    mb: 2,
                    mx: 1,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                    cursor: "pointer",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "scale(1.01)",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700}>
                    {client.companyName || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {client.generationTimestamp ? convertTimestamp(client.generationTimestamp) : "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {client.email || "N/A"}
                  </Typography>
                </Box>
              ) : (
                <TableRow
                  key={client.id}
                  onClick={() => navigate(`/customers/${client.id}`, { state: { client } })}
                  sx={{
                    cursor: "pointer",
                    transition: "background 0.2s ease, transform 0.2s ease",
                    "&:hover": {
                      backgroundColor: `${theme.palette.primary.main}15`,
                      transform: "scale(1.002)",
                    },
                  }}
                >
                  <TableCell>{client.companyName || "N/A"}</TableCell>
                  <TableCell>{client.generationTimestamp ? convertTimestamp(client.generationTimestamp) : "N/A"}</TableCell>
                  <TableCell sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{client.email || "N/A"}</TableCell>
                </TableRow>
              )
            )}
            {!isMobile && emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={3} />
              </TableRow>
            )}
          </TableBody>

          {!isMobile && (
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[10, 20]}
                  count={filteredClients.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage={t("rows_per_page")}
                  labelDisplayedRows={({ from, to, count }) => t("displayed_rows", { from, to, count: count !== -1 ? count : `more than ${to}` })}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>

        {isMobile && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <TablePaginationActions count={filteredClients.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} />
          </Box>
        )}
      </TableContainer>
    </Box>
  );
};

CustomersTable.propTypes = {
  clients: PropTypes.array.isRequired,
};

export default CustomersTable;
